import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/controls/customControls/dialog";
import { Button } from "../../../components/controls/customControls/button";
import { Input } from "../../../components/controls/customControls/input";
import { Label } from "../../../components/controls/customControls/label";
import { Textarea } from "../../../components/controls/customControls/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/controls/customControls/select";
import { Checkbox } from "../../../components/controls/customControls/checkbox";
import { AlertCircle } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  BlockOut,
  blockOutTypes,
  useScheduleData,
  detectBlockOutMultiDayInfo,
} from "../scheduleData";
const normalizeName = (name: any) => {
  if (name === null || name === undefined) return "";
  if (typeof name === "object") {
    name =
      name.name || name.label || name.id || name._id || JSON.stringify(name);
  }
  return String(name).toLowerCase().trim() || "";
};
const blockOutSchema = z
  .object({
    title: z.string().min(1, "Title is required"),
    type: z.enum(["team_event", "training", "maintenance", "break", "other"]),
    startDate: z.string().min(1, "Start date is required"),
    endDate: z.string().min(1, "End date is required"),
    startTime: z.string().optional(),
    endTime: z.string().optional(),
    isAllDay: z.boolean().optional(),
    description: z.string().optional(),
    affectedRooms: z.array(z.string()).optional(),
    affectedPharmacists: z.array(z.string()).optional(),
  })
  .refine((data) => new Date(data.endDate) >= new Date(data.startDate), {
    message: "End date must be the same or after start date",
    path: ["endDate"],
  })
  .refine(
    (data) =>
      data.isAllDay || (!!data.startTime && data.startTime.trim() !== ""),
    { message: "Start time is required", path: ["startTime"] },
  )
  .refine(
    (data) => data.isAllDay || (!!data.endTime && data.endTime.trim() !== ""),
    { message: "End time is required", path: ["endTime"] },
  );

type BlockOutForm = z.infer<typeof blockOutSchema>;

interface BlockOutModalProps {
  trigger?: React.ReactNode;
  onSave: (blockOut: BlockOutForm) => void;
  onDelete?: (blockOutId: string, otherPharmacyId?: string) => void;
  editMode?: BlockOut;
  initialDate?: string;
  initialTime?: string;
  isDarkMode?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  rooms?: any[];
  roomNames?: string[];
}

export function BlockOutModal({
  trigger,
  onSave,
  onDelete,
  editMode,
  initialDate,
  initialTime,
  isDarkMode = false,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  rooms: propsRooms,
  roomNames: propsRoomNames,
}: BlockOutModalProps) {
  const s = useScheduleData();
  const {
    pharmacists = [],
    roomNames: contextRoomNames,
    rooms: contextRooms,
    resources: contextResources = [],
    onCreateBlockOut,
    isEventCalendarLoading = false,
    validateBlockOutConflicts,
  } = (s || {}) as any;

  const normalizedRooms = useMemo(() => {
    const allResources =
      contextResources && contextResources.length > 0
        ? contextResources
        : propsRooms && propsRooms.length > 0
          ? propsRooms
          : contextRooms && contextRooms.length > 0
            ? contextRooms
            : [];

    const roomObjects = allResources.filter((r: any) => {
      if (typeof r === "string") return false;
      const resType = Number(
        r.resourceType !== undefined ? r.resourceType : r.ResourceType,
      );
      return resType === 2 || r.type === "Consultation Room" || r.isRoom;
    });

    if (roomObjects.length > 0) {
      return roomObjects.map((r: any) => ({
        id: String(r.id || r._id || r.RoomId || r.name || ""),
        name: String(r.name || r.RoomName || r.label || r.id || ""),
      }));
    }

    const names =
      propsRoomNames && propsRoomNames.length > 0
        ? propsRoomNames
        : contextRoomNames || [];
    return (names || []).map((r: any) => {
      if (typeof r === "string") {
        const foundRes = (contextResources || []).find(
          (res: any) =>
            Number(res.resourceType) === 2 &&
            String(res.name).trim().toLowerCase() ===
              String(r).trim().toLowerCase(),
        );
        return {
          id: foundRes ? String(foundRes.id || foundRes._id) : r,
          name: r,
        };
      }
      return {
        id: String(r.id || r._id || r.RoomId || r.name || ""),
        name: String(r.name || r.RoomName || r.label || r.id || ""),
      };
    });
  }, [
    propsRooms,
    contextRooms,
    contextResources,
    propsRoomNames,
    contextRoomNames,
  ]);

  const filteredPharmacistNames = useMemo(() => {
    const list =
      contextResources && contextResources.length > 0
        ? contextResources
            .filter((r: any) => {
              const resType = Number(
                r.resourceType !== undefined ? r.resourceType : r.ResourceType,
              );
              if (resType === 2 || r.type === "Consultation Room" || r.isRoom)
                return false;
              return (
                resType === 1 ||
                r.type === "Practitioner" ||
                r.type === "Staff" ||
                r.isPharmacist ||
                !r.resourceType
              );
            })
            .map((r: any) => String(r.name || r.label || r.id || "").trim())
        : (pharmacists || []).map((p: any) => {
            if (typeof p === "string") return p.trim();
            const resType = Number(
              p.resourceType !== undefined ? p.resourceType : p.ResourceType,
            );
            if (resType === 2 || p.type === "Consultation Room" || p.isRoom)
              return "";
            return String(p.name || p.label || p.id || "").trim();
          });

    return Array.from(new Set(list.filter((name: string) => name !== "")));
  }, [contextResources, pharmacists]);

  const pharmacistIdByName = useMemo(
    () =>
      new Map(
        pharmacists.map((p: any) => {
          const nameVal =
            typeof p === "string" ? p : p?.name || p?.label || p?.id || "";
          const idVal =
            typeof p === "string" ? p : p?.id || p?._id || p?.name || "";
          return [normalizeName(nameVal), String(idVal)];
        }),
      ),
    [pharmacists],
  );
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOnOpenChange
    ? controlledOnOpenChange
    : setInternalOpen;
  const [selectedRooms, setSelectedRooms] = useState<string[]>([]);
  const [selectedPharmacists, setSelectedPharmacists] = useState<string[]>([]);
  const [blockOutMode, setBlockOutMode] = useState<"single" | "multiple">(
    "single",
  );
  const [isAllDay, setIsAllDay] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    getValues,
    formState: { errors },
  } = useForm<BlockOutForm>({
    resolver: zodResolver(blockOutSchema),
    defaultValues: {
      startDate: initialDate || "",
      endDate: initialDate || "",
      startTime: initialTime || editMode?.startTime || "",
      endTime: initialTime || editMode?.endTime || "",
      affectedRooms: editMode?.affectedRooms || [],
      affectedPharmacists: editMode?.affectedPharmacists || [],
    },
  });
  const prevOpenRef = React.useRef(false);
  const prevEditIdRef = React.useRef<string | undefined>(undefined);

  // Reset form when modal opens or editMode changes
  React.useEffect(() => {
    const editId = editMode?.id || (editMode as any)?._id;
    const justOpened =
      open && (!prevOpenRef.current || prevEditIdRef.current !== editId);
    prevOpenRef.current = !!open;
    prevEditIdRef.current = editId;

    if (!open) return;

    if (justOpened) {
      if (editMode) {
        // Map affected rooms to normalized room IDs or room names
        const initialRoomIds: string[] = [];
        (editMode.affectedRooms || []).forEach((resource: any) => {
          const raw = String(resource).trim();
          if (!raw) return;
          const matchedRoom = normalizedRooms.find(
            (room) =>
              room.id === raw ||
              room.id.toLowerCase() === raw.toLowerCase() ||
              normalizeName(room.name) === normalizeName(raw),
          );
          if (matchedRoom) {
            if (!initialRoomIds.includes(matchedRoom.id)) {
              initialRoomIds.push(matchedRoom.id);
            }
          } else {
            if (!initialRoomIds.includes(raw)) {
              initialRoomIds.push(raw);
            }
          }
        });

        // Map affected pharmacists
        const initialPharmacistNames: string[] = [];
        (editMode.affectedPharmacists || []).forEach((resource: any) => {
          const raw = String(resource).trim();
          if (!raw) return;
          const matchedPharmacist = (pharmacists || []).find(
            (p: any) =>
              String(p.id || p._id || "").toLowerCase() === raw.toLowerCase() ||
              String(p.email || "").toLowerCase() === raw.toLowerCase() ||
              normalizeName(p.name || p.label || String(p)) ===
                normalizeName(raw),
          );
          const nameToPush = matchedPharmacist
            ? matchedPharmacist.name || matchedPharmacist.label
            : raw;
          if (nameToPush && !initialPharmacistNames.includes(nameToPush)) {
            initialPharmacistNames.push(nameToPush);
          }
        });

        const uniqueRooms = [...new Set(initialRoomIds)];
        const uniquePharmacists = [...new Set(initialPharmacistNames)];

        // Set mode and dates based on detectBlockOutMultiDayInfo
        const multiDayInfo = detectBlockOutMultiDayInfo(editMode);
        setBlockOutMode(multiDayInfo.isMultiDay ? "multiple" : "single");

        // Check if this is an all-day block-out
        const isAllDayBlock =
          !editMode.startTime ||
          !editMode.endTime ||
          (editMode.startTime === "08:00" && editMode.endTime === "23:59");

        setIsAllDay(isAllDayBlock);

        // Set the separated rooms and pharmacists
        setSelectedRooms(uniqueRooms);
        setSelectedPharmacists(uniquePharmacists);

        // Reset form with separated data
        const formData = {
          title: editMode.title || "",
          type: editMode.type,
          startDate:
            multiDayInfo.startDate ||
            editMode.startDate ||
            editMode?.date ||
            "",
          endDate:
            multiDayInfo.endDate ||
            editMode.endDate ||
            multiDayInfo.startDate ||
            editMode?.date ||
            "",
          startTime: isAllDayBlock ? "08:00" : editMode.startTime || "",
          endTime: isAllDayBlock ? "23:59" : editMode.endTime || "",
          isAllDay: isAllDayBlock,
          description: editMode.description || "",
          affectedRooms: uniqueRooms,
          affectedPharmacists: uniquePharmacists,
        };

        reset(formData);
      } else {
        setBlockOutMode("single");
        setIsAllDay(false);
        reset({
          startDate: initialDate || "",
          endDate: initialDate || "",
          startTime: initialTime || "",
          endTime: "",
          isAllDay: false,
          title: "",
          type: undefined,
          description: "",
          affectedRooms: [],
          affectedPharmacists: [],
        });
        setSelectedRooms([]);
        setSelectedPharmacists([]);
      }
    }
  }, [open, editMode?.id, initialDate, initialTime]);

  // Lock body scroll while modal is open to prevent background/page scrolling
  React.useEffect(() => {
    if (!open) return;
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
    };
  }, [open]);

  // Clear any previous inline error when modal closes
  React.useEffect(() => {
    if (!open) {
      setSubmitError(null);
    }
  }, [open]);

  const blockOutType = watch("type");
  const startDate = watch("startDate");
  const endDate = watch("endDate");
  const startTime = watch("startTime");
  const endTime = watch("endTime");

  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (submitError) setSubmitError(null);
  }, [
    startDate,
    endDate,
    startTime,
    endTime,
    selectedPharmacists,
    selectedRooms,
  ]);

  const onSubmit = async (data: BlockOutForm) => {
    setSubmitError(null);

    if (selectedRooms.length === 0 && selectedPharmacists.length === 0) {
      setSubmitError("You must select at least one room or pharmacist.");
      return;
    }

    try {
      const affectedPharmacists = selectedPharmacists.map(
        (pharmacist) =>
          pharmacistIdByName.get(normalizeName(pharmacist)) || pharmacist,
      );
      const formData = {
        ...data,
        startTime: data.isAllDay ? "08:00" : data.startTime,
        endTime: data.isAllDay ? "23:59" : data.endTime,
        affectedRooms: selectedRooms,
        affectedPharmacists,
      };

      if (validateBlockOutConflicts) {
        const validationResult = validateBlockOutConflicts(
          formData,
          editMode?.id,
        );
        if (!validationResult.isValid) {
          setSubmitError(validationResult.message);
          return;
        }
      }

      await Promise.resolve(onSave(formData));

      setOpen(false);
      if (!editMode) {
        reset();
        setSelectedRooms([]);
        setSelectedPharmacists([]);
        setBlockOutMode("single");
        setIsAllDay(false);
      }
    } catch (e: any) {
      const message =
        (e && (e.message || e.toString())) ||
        "Unable to create block-out. Please review the fields.";
      setSubmitError(message);
    }
  };

  // const timeSlots = useMemo(() => {
  //   const slots: string[] = [];

  //   // Generate all 48 half-hour slots
  //   for (let hour = 0; hour < 24; hour++) {
  //     for (const minute of [0, 30]) {
  //       slots.push(
  //         `${hour.toString().padStart(2, "0")}:${minute
  //           .toString()
  //           .padStart(2, "0")}`,
  //       );
  //     }
  //   }

  //   // Move everything before 08:00 to the end
  //   const startIndex = slots.indexOf("08:00");
  //   return [...slots.slice(startIndex), ...slots.slice(0, startIndex)];
  // }, []);

  const timeSlots = useMemo(() => {
    const slots: string[] = [];

    for (let hour = 8; hour < 24; hour++) {
      for (const minute of [0, 30]) {
        slots.push(
          `${hour.toString().padStart(2, "0")}:${minute
            .toString()
            .padStart(2, "0")}`,
        );
      }
    }

    return slots;
  }, []);

  const handleRoomChange = useCallback((room: string, checked: boolean) => {
    if (checked) {
      setSelectedRooms((prev) => [...prev, room]);
    } else {
      setSelectedRooms((prev) => prev.filter((r) => r !== room));
    }
  }, []);

  const handlePharmacistChange = useCallback(
    (pharmacist: string, checked: boolean) => {
      if (checked) {
        setSelectedPharmacists((prev) => [...prev, pharmacist]);
      } else {
        setSelectedPharmacists((prev) => prev.filter((p) => p !== pharmacist));
      }
    },
    [],
  );

  const handleSelectAllRooms = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectedRooms(normalizedRooms.map((r) => r.id));
      } else {
        setSelectedRooms([]);
      }
    },
    [normalizedRooms],
  );

  const handleSelectAllPharmacists = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectedPharmacists([...filteredPharmacistNames]);
      } else {
        setSelectedPharmacists([]);
      }
    },
    [filteredPharmacistNames],
  );

  // Check if all items are selected
  const allRoomsSelected =
    normalizedRooms.length > 0 &&
    selectedRooms.length === normalizedRooms.length;
  const allPharmacistsSelected =
    filteredPharmacistNames.length > 0 &&
    filteredPharmacistNames.every((name) =>
      selectedPharmacists.some(
        (selected) => normalizeName(selected) === normalizeName(name),
      ),
    );
  const someRoomsSelected =
    selectedRooms.length > 0 && selectedRooms.length < normalizedRooms.length;
  const somePharmacistsSelected =
    selectedPharmacists.length > 0 && !allPharmacistsSelected;

  const handleCheckedChange = useCallback(
    (checked) => {
      const isChecked = !!checked;
      setIsAllDay(isChecked);
      setValue("isAllDay", isChecked);
      if (isChecked) {
        setValue("startTime", "");
        setValue("endTime", "");
      }
    },
    [setValue],
  );

  const handleStartDateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setValue("startDate", value);
      setValue("endDate", value);
    },
    [setValue],
  );

  const todayStr = React.useMemo(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }, []);

  useEffect(() => {
    if (!open) reset();
  }, [open]);

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>{trigger}</DialogTrigger>
        <DialogContent
          className={`sm:max-w-[600px] max-h-[90vh] overscroll-contain transition-colors duration-300 flex flex-col ${
            isDarkMode
              ? "bg-gray-800 border-gray-600 text-white"
              : "bg-white border-gray-200 text-gray-900"
          }`}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <DialogHeader>
            <DialogTitle
              className={`text-lg font-semibold transition-colors duration-300 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              {editMode ? "Edit Block-Out Period" : "New Block-Out Period"}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 px-1 overflow-y-auto overscroll-contain">
            <form onSubmit={handleSubmit(onSubmit)} className="pb-2 space-y-6">
              {/* Title and Type */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label
                    className={`text-sm font-medium transition-colors duration-300 ${
                      isDarkMode ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    Title *
                  </Label>
                  <Input
                    {...register("title")}
                    placeholder="e.g., Team Meeting"
                    className="w-full"
                    disabled={isEventCalendarLoading}
                  />
                  {errors.title && (
                    <p
                      className={`text-sm transition-colors duration-300 ${
                        isDarkMode ? "text-red-400" : "text-red-600"
                      }`}
                    >
                      {errors.title.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label
                    className={`text-sm font-medium transition-colors duration-300 ${
                      isDarkMode ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    Type *
                  </Label>
                  <Select
                    value={blockOutType}
                    onValueChange={(value: any) => setValue("type", value)}
                    disabled={isEventCalendarLoading}
                  >
                    <SelectTrigger
                      className="w-full"
                      disabled={isEventCalendarLoading}
                    >
                      <SelectValue placeholder="Select type..." />
                    </SelectTrigger>
                    <SelectContent>
                      {blockOutTypes.map((type, index) => (
                        <SelectItem
                          key={`${type.value}-${index}`}
                          value={type.value}
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className={`w-3 h-3 rounded-full ${type.color}`}
                            ></div>
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.type && (
                    <p
                      className={`text-sm transition-colors duration-300 ${
                        isDarkMode ? "text-red-400" : "text-red-600"
                      }`}
                    >
                      {errors.type.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Block-Out Mode Selection */}
              <div className="space-y-3">
                <Label
                  className={`text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  Block-Out Duration
                </Label>
                <div className="flex gap-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="single-day"
                      checked={blockOutMode === "single"}
                      disabled={isEventCalendarLoading}
                      onChange={useCallback(() => {
                        setBlockOutMode("single");
                        setIsAllDay(false);
                        if (startDate) setValue("endDate", startDate);
                      }, [setValue, watch])}
                      className="text-blue-600"
                    />
                    <label
                      htmlFor="single-day"
                      className={`text-sm cursor-pointer transition-colors duration-300 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Single Day
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="multiple-days"
                      checked={blockOutMode === "multiple"}
                      disabled={isEventCalendarLoading}
                      onChange={useCallback(() => {
                        setBlockOutMode("multiple");
                        setIsAllDay(true);
                        setValue("isAllDay", true);
                        setValue("startTime", "");
                        setValue("endTime", "");
                      }, [setValue])}
                      className="text-blue-600"
                    />
                    <label
                      htmlFor="multiple-days"
                      className={`text-sm cursor-pointer transition-colors duration-300 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Multiple Days
                    </label>
                  </div>
                </div>
              </div>

              {/* Date Selection */}
              <div className="space-y-4">
                {blockOutMode === "single" ? (
                  <div className="space-y-2">
                    <Label
                      className={`text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      Date *
                    </Label>
                    <Input
                      type="date"
                      {...register("startDate")}
                      onChange={handleStartDateChange}
                      className="w-full"
                      disabled={isEventCalendarLoading}
                      min={todayStr}
                    />
                    {errors.startDate && (
                      <p
                        className={`text-sm transition-colors duration-300 ${
                          isDarkMode ? "text-red-400" : "text-red-600"
                        }`}
                      >
                        {errors.startDate.message}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label
                        className={`text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-200" : "text-gray-700"
                        }`}
                      >
                        Start Date *
                      </Label>
                      <Input
                        type="date"
                        {...register("startDate")}
                        className="w-full"
                        disabled={isEventCalendarLoading}
                        min={todayStr}
                      />
                      {errors.startDate && (
                        <p
                          className={`text-sm transition-colors duration-300 ${
                            isDarkMode ? "text-red-400" : "text-red-600"
                          }`}
                        >
                          {errors.startDate.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label
                        className={`text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-200" : "text-gray-700"
                        }`}
                      >
                        End Date *
                      </Label>
                      <Input
                        type="date"
                        {...register("endDate")}
                        className="w-full"
                        disabled={isEventCalendarLoading}
                        min={todayStr}
                      />
                      {errors.endDate && (
                        <p
                          className={`text-sm transition-colors duration-300 ${
                            isDarkMode ? "text-red-400" : "text-red-600"
                          }`}
                        >
                          {errors.endDate.message}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Time Configuration */}
                {blockOutMode === "multiple" ? (
                  <div className="space-y-3">
                    <Label
                      className={`text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      Time Configuration
                    </Label>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="all-day"
                        checked={isAllDay}
                        onCheckedChange={handleCheckedChange}
                        disabled={isEventCalendarLoading}
                      />
                      <label
                        htmlFor="all-day"
                        className={`text-sm cursor-pointer transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        All Day (blocks entire day)
                      </label>
                    </div>

                    {!isAllDay && (
                      <div className="grid grid-cols-2 gap-4 mt-3">
                        <div className="space-y-2">
                          <Label
                            className={`text-sm font-medium transition-colors duration-300 ${
                              isDarkMode ? "text-gray-200" : "text-gray-700"
                            }`}
                          >
                            Start Time *
                          </Label>
                          <Select
                            value={startTime || ""}
                            onValueChange={(value) =>
                              setValue("startTime", value)
                            }
                            disabled={isEventCalendarLoading}
                          >
                            <SelectTrigger
                              className="w-full"
                              disabled={isEventCalendarLoading}
                            >
                              <SelectValue placeholder="Start time" />
                            </SelectTrigger>
                            <SelectContent>
                              {timeSlots.map((time, index) => (
                                <SelectItem
                                  key={`${time}-${index}`}
                                  value={time}
                                >
                                  {time}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {errors.startTime && (
                            <p
                              className={`text-sm transition-colors duration-300 ${
                                isDarkMode ? "text-red-400" : "text-red-600"
                              }`}
                            >
                              {errors.startTime.message}
                            </p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label
                            className={`text-sm font-medium transition-colors duration-300 ${
                              isDarkMode ? "text-gray-200" : "text-gray-700"
                            }`}
                          >
                            End Time *
                          </Label>
                          <Select
                            value={endTime || ""}
                            onValueChange={(value) =>
                              setValue("endTime", value)
                            }
                            disabled={isEventCalendarLoading}
                          >
                            <SelectTrigger
                              className="w-full"
                              disabled={isEventCalendarLoading}
                            >
                              <SelectValue placeholder="End time" />
                            </SelectTrigger>
                            <SelectContent>
                              {[
                                ...timeSlots,
                                ...(endTime === "23:59" &&
                                !timeSlots?.includes("23:59")
                                  ? ["23:59"]
                                  : []),
                              ]?.map((time, index) => (
                                <SelectItem
                                  key={`${time}-${index}`}
                                  value={time}
                                >
                                  {time}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {errors.endTime && (
                            <p
                              className={`text-sm transition-colors duration-300 ${
                                isDarkMode ? "text-red-400" : "text-red-600"
                              }`}
                            >
                              {errors.endTime.message}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label
                        className={`text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-200" : "text-gray-700"
                        }`}
                      >
                        Start Time *
                      </Label>
                      <Select
                        value={startTime || ""}
                        onValueChange={(value) => setValue("startTime", value)}
                        disabled={isEventCalendarLoading}
                      >
                        <SelectTrigger
                          className="w-full"
                          disabled={isEventCalendarLoading}
                        >
                          <SelectValue placeholder="Start time" />
                        </SelectTrigger>
                        <SelectContent>
                          {timeSlots.map((time, index) => (
                            <SelectItem key={`${time}-${index}`} value={time}>
                              {time}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.startTime && (
                        <p
                          className={`text-sm transition-colors duration-300 ${
                            isDarkMode ? "text-red-400" : "text-red-600"
                          }`}
                        >
                          {errors.startTime.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label
                        className={`text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-200" : "text-gray-700"
                        }`}
                      >
                        End Time *
                      </Label>
                      <Select
                        value={endTime || ""}
                        onValueChange={(value) => setValue("endTime", value)}
                        disabled={isEventCalendarLoading}
                      >
                        <SelectTrigger
                          className="w-full"
                          disabled={isEventCalendarLoading}
                        >
                          <SelectValue placeholder="End time" />
                        </SelectTrigger>
                        <SelectContent>
                          {timeSlots.map((time, index) => (
                            <SelectItem key={`${time}-${index}`} value={time}>
                              {time}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.endTime && (
                        <p
                          className={`text-sm transition-colors duration-300 ${
                            isDarkMode ? "text-red-400" : "text-red-600"
                          }`}
                        >
                          {errors.endTime.message}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label
                  className={`text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  Description
                </Label>
                <Textarea
                  {...register("description")}
                  placeholder="Additional details about this block-out period..."
                  className="w-full"
                  rows={3}
                  disabled={isEventCalendarLoading}
                />
              </div>

              {/* Affected Rooms */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label
                    className={`text-sm font-medium transition-colors duration-300 ${
                      isDarkMode ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    Affected Rooms
                  </Label>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="select-all-rooms"
                      checked={allRoomsSelected}
                      disabled={isEventCalendarLoading}
                      ref={(el) => {
                        if (el) {
                          (el as any).indeterminate = someRoomsSelected;
                        }
                      }}
                      onCheckedChange={(checked) =>
                        handleSelectAllRooms(!!checked)
                      }
                    />
                    <label
                      className={`text-sm transition-colors duration-300 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Select All
                    </label>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 overflow-y-auto max-h-32">
                  {normalizedRooms.map((room, index) => (
                    <div
                      key={`${room.id}-${index}`}
                      className="flex items-center space-x-2"
                    >
                      <Checkbox
                        id={`room-${room.id}`}
                        checked={selectedRooms.includes(room.id)}
                        disabled={isEventCalendarLoading}
                        onCheckedChange={(checked) =>
                          handleRoomChange(room.id, !!checked)
                        }
                      />
                      <label
                        htmlFor={`room-${room.id}`}
                        className={`text-sm cursor-pointer transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        {room.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Affected Pharmacists */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label
                    className={`text-sm font-medium transition-colors duration-300 ${
                      isDarkMode ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    Affected Pharmacists
                  </Label>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="select-all-pharmacists"
                      checked={allPharmacistsSelected}
                      disabled={isEventCalendarLoading}
                      ref={(el) => {
                        if (el) {
                          (el as any).indeterminate = somePharmacistsSelected;
                        }
                      }}
                      onCheckedChange={(checked) =>
                        handleSelectAllPharmacists(!!checked)
                      }
                    />
                    <label
                      className={`text-sm transition-colors duration-300 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Select All
                    </label>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 overflow-y-auto max-h-32">
                  {filteredPharmacistNames.map((pharmacist, index) => (
                    <div
                      key={`${pharmacist}-${index}`}
                      className="flex items-center space-x-2"
                    >
                      <Checkbox
                        id={`pharmacist-${pharmacist}`}
                        checked={selectedPharmacists.includes(pharmacist)}
                        disabled={isEventCalendarLoading}
                        onCheckedChange={(checked) =>
                          handlePharmacistChange(pharmacist, !!checked)
                        }
                      />
                      <label
                        htmlFor={`pharmacist-${pharmacist}`}
                        className={`text-sm cursor-pointer transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        {pharmacist}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Inline form error (e.g., duplicate/validation/conflict) */}
              {submitError && (
                <div
                  className={`mt-2 p-3 rounded-md border flex items-center gap-2 text-sm font-medium ${
                    isDarkMode
                      ? "bg-red-900/30 border-red-700 text-red-200"
                      : "bg-red-50 border-red-200 text-red-700"
                  }`}
                >
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{submitError}</span>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(false);
                  }}
                  className="flex-1"
                  disabled={isEventCalendarLoading}
                >
                  Cancel
                </Button>
                {editMode && onDelete && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => setShowDeleteConfirm(true)}
                    className="flex-1"
                    disabled={isEventCalendarLoading}
                  >
                    Delete
                  </Button>
                )}
                <Button
                  type="submit"
                  className="flex-1 text-white bg-slate-900 hover:bg-slate-800"
                  disabled={isEventCalendarLoading}
                >
                  {isEventCalendarLoading ? (
                    <span className="inline-flex items-center gap-1">
                      {editMode ? "Updating" : "Creating"}
                      <span className="">...</span>
                    </span>
                  ) : editMode ? (
                    "Update Block-Out"
                  ) : (
                    "Create Block-Out"
                  )}
                </Button>
              </div>
            </form>
          </div>

          {/* Delete Confirmation Dialog */}
          {showDeleteConfirm && editMode && onDelete && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
              <div
                className={`p-6 rounded-lg max-w-md w-full mx-4 ${
                  isDarkMode
                    ? "bg-gray-800 text-white"
                    : "bg-white text-gray-900"
                }`}
              >
                <h3 className="mb-4 text-lg font-semibold">Delete Block-Out</h3>
                <p
                  className={`${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  } mb-6`}
                >
                  Are you sure you want to delete this block-out period? This
                  action cannot be undone.
                </p>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDeleteConfirm(false);
                    }}
                    className="flex-1"
                    disabled={isEventCalendarLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete?.(editMode.id, editMode.otherPharmacyId);
                      setShowDeleteConfirm(false);
                      setOpen(false);
                    }}
                    className="flex-1"
                    disabled={isEventCalendarLoading}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
